import PropTypes from 'prop-types';
import {
	useState,
	useRef,
	useEffect,
	Fragment,
	useCallback,
	useMemo,
} from 'react';
import usePolling from '../../hooks/usePolling';
import { useParams } from 'react-router';
import useDealStore from '../../stores/dealStore';
import {
	Box,
	Button,
	Typography,
	Paper,
	Grid,
	List,
	ListItem,
	ListItemText,
	Divider,
	Link,
	Chip,
	IconButton,
	Snackbar,
	Alert,
	TextField,
	InputAdornment,
	FormControl,
	Select,
	MenuItem,
	CircularProgress,
	Tooltip,
	Popper,
	ClickAwayListener,
} from '@mui/material';
import StatusChip from '../common/StatusChip';
import PollingStatus from '../common/PollingStatus';
import SourceIcon from './SourceIcon';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoIcon from '@mui/icons-material/Info';
import LabelIcon from '@mui/icons-material/Label';
import { authedApiFetch } from '../../../../utils/Api';
import AMFIDrawer from '../AMFI/AMFIDrawer';
import FileUploader from './FileUploader';
import { parseSelectedText } from '../../utils/amfiUtils';

/**
 * SourcesSection component to display sources and add new files
 * @param {Array} sources - List of information sources
 * @param {Array} promptLibrary - List of predefined prompts
 */
const SourcesSection = ({ sources = [], promptLibrary = [] }) => {
	// Colors for AMFI types
	const amfiColors = {
		attribute: {
			bg: 'rgba(39, 164, 249, 0.2)',
			border: '#27A4F9',
			name: 'Sky',
		},
		metric: {
			bg: 'rgba(0, 128, 119, 0.2)',
			border: '#008077',
			name: 'Pine',
		},
		fact: {
			bg: 'rgba(247, 189, 0, 0.2)',
			border: '#F7BD00',
			name: 'Goldenrod',
		},
		insight: {
			bg: 'rgba(87, 89, 238, 0.2)',
			border: '#5759EE',
			name: 'Indigo',
		},
	};

	const typeKeywords = {
		document: ['document'],
		website: ['website', 'web'],
		news: ['news', 'article'],
		internal: ['internal'],
		subscription: ['subscription'],
	};

	const mapSourceType = (sourceType) => {
		if (!sourceType) return 'document';
		const lowerType = sourceType.toLowerCase();
		for (const [type, keywords] of Object.entries(typeKeywords)) {
			if (keywords.some((keyword) => lowerType.includes(keyword))) return type;
		}
		return 'document';
	};

	const [selectedPrompt, setSelectedPrompt] = useState('');
	const [query, setQuery] = useState('');
	const [sourceList, setSourceList] = useState(sources);
	const pollInterval = 30; // seconds
	const { sourceMonitors, fetchSourceMonitors } = useDealStore();
	const [error, setError] = useState(false);
	const { tenantId, dealId } = useParams();

	// AIMe query states
	const [aimeResponse, setAimeResponse] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [queryError, setQueryError] = useState(null);

	// Text selection states
	const [selectedText, setSelectedText] = useState('');
	const [anchorEl, setAnchorEl] = useState(null);
	const [taggedSegments, setTaggedSegments] = useState([]);
	const [taggingSuccess, setTaggingSuccess] = useState('');
	const responseRef = useRef(null);

	// Tag editing states
	const [showTagEditor, setShowTagEditor] = useState(false);
	const [currentTagType, setCurrentTagType] = useState('');
	const [parsedContent, setParsedContent] = useState({});

	// Category states
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('');

	const showSelectionMenu = Boolean(anchorEl);

	// Handler for when new sources are added through the FileUploader
	const handleNewSourcesAdded = (newSources) => {
		setSourceList((prevList) => [...prevList, ...newSources]);
		// Use the refresh function from our custom hook instead of fetchStatuses
		refresh();
	};

	// Check if any sources need polling (pending or processing)
	const hasPendingSources = useMemo(() => {
		if (!sourceMonitors || sourceMonitors.length === 0) return true;
		return sourceMonitors.some((monitor) =>
			['pending', 'processing'].includes(monitor.status)
		);
	}, [sourceMonitors]);

	// Define the polling function
	const pollFunction = useCallback(async () => {
		if (!tenantId || !dealId) return;
		await fetchSourceMonitors(tenantId, dealId);
	}, [tenantId, dealId, fetchSourceMonitors]);

	// Use our custom polling hook
	const { countdown, isPolling, refresh } = usePolling(
		pollFunction,
		pollInterval,
		hasPendingSources && sourceList.length > 0,
		[sourceList.length] // Add sourceList.length as a dependency
	);

	// Handle manual refresh
	const handleManualRefresh = useCallback(() => {
		refresh();
	}, [refresh]);

	// Initial setup with sources prop on first mount
	useEffect(() => {
		if (sources.length > 0 && sourceList.length === 0) {
			setSourceList(sources);
		}
	}, [sources, sourceList.length]);

	// Sync sourceList with sourceMonitors when they change
	useEffect(() => {
		// Skip if no source monitors or if they're empty
		if (!sourceMonitors || sourceMonitors.length === 0) return;

		setSourceList((prevList) => {
			// Check if we need to update the list
			const needsUpdate =
				prevList.length !== sourceMonitors.length ||
				sourceMonitors.some((monitor) => {
					const existing = prevList.find((s) => s.id === monitor.id);
					return (
						!existing ||
						existing.type !== monitor.type ||
						existing.format !== monitor.format ||
						existing.status !== monitor.status
					);
				});

			// If no update needed, return the previous list
			if (!needsUpdate) return prevList;

			// Create a map of existing sources for quick lookup
			const existingSources = new Map(
				prevList.map((source) => [source.id, source])
			);

			// Merge existing sources with updated monitors
			return sourceMonitors.map((monitor) => ({
				...(existingSources.get(monitor.id) || {}),
				...monitor,
				// Ensure we have the latest type and format from the monitor
				type: monitor.type || existingSources.get(monitor.id)?.type,
				format: monitor.format || existingSources.get(monitor.id)?.format,
			}));
		});
	}, [sourceMonitors]);

	// Helper function to get status for a source - memoize to prevent recalculations
	const getSourceStatus = useCallback(
		(sourceId) => {
			if (!sourceMonitors || !sourceId) return 'pending';
			const monitor = sourceMonitors.find((m) => m.id === sourceId);
			return monitor?.status || 'pending';
		},
		[sourceMonitors]
	);

	const handlePromptChange = (event) => {
		const value = event.target.value;
		setSelectedPrompt(value.name);
		setQuery(value.content);
	};

	const handleAsk = async () => {
		if (!query.trim()) return;

		setIsLoading(true);
		setQueryError(null);

		try {
			// Development override - use known working Salesforce ID for testing
			// This will be used only in development mode
			// const testDealId = process.env.NODE_ENV === 'development'
			//   ? 'a08Hp00001WpxmxIAB'
			//   : dealId;

			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${dealId}/query_aime`,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				payload: {
					query: query.trim(),
				},
			});

			setAimeResponse(response.predictions);
			setIsLoading(false);
		} catch (error) {
			console.error('AIMe query error:', error);
			setQueryError(`Error querying AIMe: ${error.message || 'Unknown error'}`);
			setIsLoading(false);
		}
	};

	const handleCloseSnackbar = () => {
		setError(false);
		setTaggingSuccess('');
	};

	// Fetch categories when component mounts
	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await authedApiFetch({
					endpoint: `/tenants/${tenantId}/config_lists?list_type=categories`,
					method: 'GET',
				});

				// The API returns an array where the first item contains config_list_items
				if (
					response &&
					Array.isArray(response) &&
					response.length > 0 &&
					response[0].config_list_items &&
					response[0].config_list_items.length > 0
				) {
					setCategories(response[0].config_list_items);
					// Set the first category as default if available
					if (
						response[0].config_list_items[0] &&
						response[0].config_list_items[0].id
					) {
						setSelectedCategory(response[0].config_list_items[0].id);
					}
				}
			} catch (error) {
				console.error('Error fetching categories:', error);
			}
		};

		fetchCategories();
	}, [tenantId]);

	// Handle text selection
	const handleTextSelection = (_event) => {
		const selection = window.getSelection();
		if (selection && selection.toString().trim().length > 0) {
			setSelectedText(selection.toString().trim());

			// Use the selection range as the anchor element for more precise positioning
			if (selection.rangeCount > 0) {
				// Create a position based on the selection coordinates
				const range = selection.getRangeAt(0);
				const rect = range.getBoundingClientRect();

				// Store both the range and its position
				setAnchorEl({
					clientWidth: rect.width,
					clientHeight: rect.height,
					getBoundingClientRect: () => rect,
				});
			}
		} else {
			setAnchorEl(null);
		}
	};

	// Handle scrolling to reposition the popup when needed
	useEffect(() => {
		const handleScroll = () => {
			// If a popup is shown and text is selected, update its position
			if (
				anchorEl &&
				window.getSelection().toString().trim() === selectedText
			) {
				const selection = window.getSelection();
				if (selection.rangeCount > 0) {
					const range = selection.getRangeAt(0);
					const rect = range.getBoundingClientRect();

					setAnchorEl({
						clientWidth: rect.width,
						clientHeight: rect.height,
						getBoundingClientRect: () => rect,
					});
				}
			}
		};

		// Add scroll event listener
		window.addEventListener('scroll', handleScroll, { passive: true });

		// Cleanup
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [anchorEl, selectedText]);

	// Close the selection menu
	const handleCloseSelectionMenu = () => {
		setAnchorEl(null);
	};

	// Initiates the tag creation process by showing the editor dialog
	const handleAddTag = (type) => {
		// Parse the selected text into appropriate fields based on type
		const parsed = parseSelectedText(selectedText, type);

		// Setup the editing form with the parsed data
		setCurrentTagType(type);
		setParsedContent(parsed);
		setSelectedCategory('');

		// Close the selection menu and open the tag editor
		setAnchorEl(null);
		setShowTagEditor(true);
	};

	// Handle form cancellation
	const handleFormCancel = () => {
		setShowTagEditor(false);
	};

	// Handle AMFIForm submission
	const handleFormSubmit = async (formData) => {
		const backendType = currentTagType.toLowerCase();

		try {
			const endpoint = `/tenants/${tenantId}/Deal/${dealId}/object_${backendType}s`;

			// Create the appropriate payload
			// Note: No need for complex switch statements as AMFIForm already handles
			// the correct field names based on type (name vs metric_name, value vs content)

			// Add category from selection if it wasn't included in the form
			const payload = {
				...formData,
				category: formData.category || selectedCategory || null,
			};

			// Special handling for facts and insights to combine name and value
			if (
				(backendType === 'fact' || backendType === 'insight') &&
				!payload.content
			) {
				// If content not directly provided, combine name and value
				if (payload.name && payload.value) {
					payload.content = `${payload.name}: ${payload.value}`;
					delete payload.name;
					delete payload.value;
				}
			}

			const response = await authedApiFetch({
				endpoint,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				payload: {
					[`object_${backendType}`]: payload,
				},
			});

			// If successful, add to the displayed tags
			if (response) {
				setTaggedSegments((prev) => [
					...prev,
					{
						id: response.id,
						text: selectedText,
						type: backendType,
						name:
							backendType === 'metric'
								? payload.metric_name
								: backendType === 'fact' || backendType === 'insight'
									? 'Content'
									: payload.name || '',
					},
				]);

				// Show success message
				setTaggingSuccess(`${currentTagType} created successfully!`);

				// Reset and close the form
				setShowTagEditor(false);
			}
		} catch (error) {
			console.error(`Error creating ${currentTagType}:`, error);
			setError(
				`Failed to create ${currentTagType}: ${error.message || 'Unknown error'}`
			);
			// Still add to UI for better UX, but mark as pending/local only
			setTaggedSegments((prev) => [
				...prev,
				{
					text: selectedText,
					type: backendType,
					id: Date.now(),
					name: parsedContent.name,
					value: parsedContent.value,
					pendingSync: true, // Mark as pending server sync
				},
			]);

			setShowTagEditor(false);
		}
	};

	// Highlight tagged segments in text
	const renderHighlightedText = (text) => {
		if (!text || !taggedSegments.length) return text;

		// Create segments based on tagged parts
		let lastIndex = 0;
		const segments = [];
		let resultText = text;

		// Sort by starting position to ensure correct order
		const sortedSegments = [...taggedSegments].sort((a, b) => {
			return text.indexOf(a.text) - text.indexOf(b.text);
		});

		for (const segment of sortedSegments) {
			const startIndex = resultText.indexOf(segment.text);
			if (startIndex === -1) continue;

			// Add any text before this segment
			if (startIndex > lastIndex) {
				segments.push({
					text: resultText.substring(lastIndex, startIndex),
					isTagged: false,
				});
			}

			// Add the tagged segment
			segments.push({
				text: segment.text,
				isTagged: true,
				type: segment.type,
				id: segment.id,
			});

			lastIndex = startIndex + segment.text.length;
		}

		// Add any remaining text after all tagged segments
		if (lastIndex < resultText.length) {
			segments.push({
				text: resultText.substring(lastIndex),
				isTagged: false,
			});
		}

		return (
			<>
				{segments.map((segment, index) => {
					if (!segment.isTagged) {
						return <span key={index}>{segment.text}</span>;
					}

					return (
						<Tooltip
							key={segment.id || index}
							title={`Tagged as ${segment.type}`}
							placement='top'
						>
							<span
								className={`tagged-${segment.type}`}
								style={{
									backgroundColor:
										amfiColors[segment.type]?.bg || amfiColors.insight.bg,
									borderBottom: `2px solid ${amfiColors[segment.type]?.border || amfiColors.insight.border}`,
									padding: '2px 0',
									cursor: 'default',
								}}
							>
								{segment.text}
							</span>
						</Tooltip>
					);
				})}
			</>
		);
	};

	return (
		<Box>
			<Grid container spacing={1.5}>
				{/* Left column - Knowledge Base Query Interface */}
				<Grid item xs={12} md={9}>
					<Paper variant='outlined' sx={{ mb: 1.5, p: 2 }}>
						<TextField
							fullWidth
							placeholder='Ask a question about this deal...'
							variant='outlined'
							size='small'
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							sx={{ mb: 1 }}
							multiline
							rows={3}
							InputProps={{
								endAdornment: (
									<InputAdornment position='end'>
										<IconButton
											edge='end'
											color='primary'
											onClick={handleAsk}
											disabled={!query.trim()}
										>
											<SendIcon />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>

						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
							<FormControl sx={{ minWidth: 160 }} size='small'>
								<Select
									displayEmpty
									value={selectedPrompt}
									onChange={handlePromptChange}
									sx={{ fontSize: '0.875rem' }}
									renderValue={(selected) => {
										if (selected === '') {
											return <em>Prompt Library</em>;
										}
										return selected;
									}}
								>
									<MenuItem value=''>
										<em>Prompt Library</em>
									</MenuItem>
									{promptLibrary.map((prompt, index) => (
										<MenuItem key={index} value={prompt}>
											{prompt.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					</Paper>

					<Paper variant='outlined' sx={{ p: 2 }}>
						<Paper
							variant='outlined'
							sx={{
								p: 2,
								minHeight: 250,
								maxHeight: 400,
								overflow: 'auto',
								bgcolor: 'background.default',
							}}
						>
							{isLoading ? (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: '100%',
									}}
								>
									<CircularProgress />
								</Box>
							) : queryError ? (
								<Typography color='error' align='center'>
									{queryError}
								</Typography>
							) : !aimeResponse ? (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: '100%',
									}}
								>
									<Typography color='text.secondary' align='center'>
										Ask a question to see results here
									</Typography>
								</Box>
							) : (
								<Box ref={responseRef} onMouseUp={handleTextSelection}>
									<Typography variant='h6' gutterBottom>
										Answer:
										<Tooltip title='Select text to add as attribute, metric, fact or insight'>
											<IconButton size='small' sx={{ ml: 1 }}>
												<InfoIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									</Typography>
									<Typography sx={{ whiteSpace: 'pre-wrap' }}>
										{renderHighlightedText(aimeResponse.answer)}
									</Typography>

									{aimeResponse.source_documents && (
											<>
												<Typography variant='subtitle1' sx={{ mt: 2 }}>
													Sources:
												</Typography>
												<Box
													component='pre'
													sx={{
														p: 2,
														bgcolor: 'background.paper',
														border: '1px solid',
														borderColor: 'divider',
														borderRadius: 1,
														fontSize: '0.75rem',
														overflowX: 'auto',
														whiteSpace: 'pre-wrap',
													}}
												>
													{Array.isArray(aimeResponse.source_documents)
                                                        ? aimeResponse.source_documents.join('\n\n')
                                                        : typeof aimeResponse.source_documents === 'object'
                                                          ? JSON.stringify(aimeResponse.source_documents, null, 2)
                                                          : String(aimeResponse.source_documents)}
												</Box>
											</>
										)}

									{/* Text Selection Menu */}
									<Popper
										open={showSelectionMenu}
										anchorEl={anchorEl}
										placement='top'
										modifiers={[
											{ name: 'flip', enabled: true },
											{
												name: 'preventOverflow',
												enabled: true,
												options: { boundary: document.body },
											},
											{ name: 'offset', options: { offset: [0, 8] } },
										]}
										sx={{ zIndex: 1300 }}
									>
										<ClickAwayListener onClickAway={handleCloseSelectionMenu}>
											<Paper sx={{ p: 1, display: 'flex', gap: 1 }}>
												<Tooltip title='Add as Attribute'>
													<Button
														size='small'
														variant='outlined'
														startIcon={<LabelIcon />}
														onClick={() => handleAddTag('Attribute')}
														sx={{
															color: amfiColors.attribute.border,
															borderColor: amfiColors.attribute.border,
														}}
													>
														Attribute
													</Button>
												</Tooltip>
												<Tooltip title='Add as Metric'>
													<Button
														size='small'
														variant='outlined'
														startIcon={<BarChartIcon />}
														onClick={() => handleAddTag('Metric')}
														sx={{
															color: amfiColors.metric.border,
															borderColor: amfiColors.metric.border,
														}}
													>
														Metric
													</Button>
												</Tooltip>
												<Tooltip title='Add as Fact'>
													<Button
														size='small'
														variant='outlined'
														startIcon={<BookmarkIcon />}
														onClick={() => handleAddTag('Fact')}
														sx={{
															color: amfiColors.fact.border,
															borderColor: amfiColors.fact.border,
														}}
													>
														Fact
													</Button>
												</Tooltip>
												<Tooltip title='Add as Insight'>
													<Button
														size='small'
														variant='outlined'
														startIcon={<InfoIcon />}
														onClick={() => handleAddTag('Insight')}
														sx={{
															color: amfiColors.insight.border,
															borderColor: amfiColors.insight.border,
														}}
													>
														Insight
													</Button>
												</Tooltip>
												<IconButton
													size='small'
													onClick={handleCloseSelectionMenu}
												>
													<CloseIcon fontSize='small' />
												</IconButton>
											</Paper>
										</ClickAwayListener>
									</Popper>
								</Box>
							)}
						</Paper>
					</Paper>
				</Grid>

				{/* Right column - Sources List */}
				<Grid item xs={12} md={3}>
					<Paper variant='outlined' sx={{ p: 1, height: '100%' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 1, pl: 1 }}>
							<Typography
								variant='subtitle1'
								sx={{
									display: 'flex',
									alignItems: 'center',
									lineHeight: 1,
									my: 0,
								}}
							>
								Sources
							</Typography>
							<FileUploader
								tenantId={tenantId}
								objectId={dealId}
								onFilesAdded={handleNewSourcesAdded}
								buttonProps={{
									sx: {
										ml: 1,
										p: 0.75,
										my: 0,
									},
								}}
							/>
						</Box>

						{sourceList.length > 0 && hasPendingSources && (
							<PollingStatus
								pollInterval={pollInterval}
								countdown={countdown}
								isPolling={isPolling}
								onRefresh={handleManualRefresh}
							/>
						)}
						<List dense sx={{ maxHeight: 450, overflow: 'auto' }}>
							{sourceList.map((source) => (
								<Fragment key={source.id}>
									<ListItem
										component={Link}
										href={source.url}
										sx={{
											py: 0.5,
											cursor: 'pointer',
											'&:hover': {
												bgcolor: 'action.hover',
											},
										}}
									>
										<SourceIcon type={mapSourceType(source.type)} />
										<ListItemText
											primary={
												<Box
													sx={{ display: 'flex', ml: 1, alignItems: 'center' }}
												>
													{source.name ||
														source.source?.file_name ||
														'Untitled Source'}
													{source.format && (
														<Chip
															label={source.format}
															size='small'
															sx={{
																ml: 1,
																height: 20,
																fontSize: '0.7rem',
																textTransform: 'uppercase',
															}}
														/>
													)}
													<StatusChip status={getSourceStatus(source.id)} />
												</Box>
											}
										/>
									</ListItem>
									<Divider component='li' />
								</Fragment>
							))}
						</List>
					</Paper>
				</Grid>
			</Grid>

			{/* Tag Editor Drawer */}
			<AMFIDrawer
				open={showTagEditor}
				onClose={() => setShowTagEditor(false)}
				type={currentTagType.toLowerCase()}
				item={{
					// Pass the parsed content from the utility function
					...parsedContent,
					// Add category from form selection
					category: selectedCategory,
				}}
				categories={categories}
				onSubmit={handleFormSubmit}
				onCancel={handleFormCancel}
			/>

			{/* Tagging success notification */}
			<Snackbar
				open={!!taggingSuccess}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity='success'
					sx={{ width: '100%' }}
				>
					{taggingSuccess}
				</Alert>
			</Snackbar>

			{/* Error notification */}
			<Snackbar
				open={!!error}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity='error'
					sx={{ width: '100%' }}
				>
					{error}
				</Alert>
			</Snackbar>
		</Box>
	);
};

SourcesSection.propTypes = {
	sources: PropTypes.array.isRequired,
	promptLibrary: PropTypes.array.isRequired,
};

export default SourcesSection;
